/**
 * facets.js — sticky filter bar (Stanley Furniture / Distinctive "Lineage")
 *
 * Desktop: each filter is an inline dropdown; any change applies immediately (debounced) and the
 * results re-render in place. Mobile: the same filters open in a bottom sheet as accordions.
 * Only ONE set of inputs exists, so the form data is always exactly what the customer sees.
 */

const FACETS_DESKTOP_MQ = window.matchMedia('(min-width: 1068px)');
const facetsIsDesktop = () => FACETS_DESKTOP_MQ.matches;

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.debouncedOnSubmit = debounce((event) => this.onSubmitHandler(event), 400);
    this.form.addEventListener('input', this.debouncedOnSubmit);
    this.form.addEventListener('submit', (event) => event.preventDefault());
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-facets-close]')) {
        event.preventDefault();
        this.closeDrawer();
      }
    });
  }

  connectedCallback() {
    this.dropdowns = this.querySelector('facet-dropdowns');

    this.onDocumentClick = (event) => {
      if (!facetsIsDesktop()) return;
      if (!event.target.closest('facet-dropdown')) FacetFiltersForm.closeDropdowns();
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      FacetFiltersForm.closeDropdowns();
      this.closeDrawer();
    };
    this.onBreakpoint = () => {
      FacetFiltersForm.closeDropdowns();
      this.closeDrawer();
    };
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    FACETS_DESKTOP_MQ.addEventListener('change', this.onBreakpoint);

    if (this.dropdowns) {
      // Tap on the dimmed backdrop (the <facet-dropdowns> element itself on mobile) closes the sheet.
      this.dropdowns.addEventListener('click', (event) => {
        if (event.target === this.dropdowns && !facetsIsDesktop()) this.closeDrawer();
      });
    }

    this.setupStickyOffset();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    FACETS_DESKTOP_MQ.removeEventListener('change', this.onBreakpoint);
  }

  /* The bar sticks directly under whatever part of the header is on screen. Measured, not assumed. */
  setupStickyOffset() {
    const header = document.getElementById('header');
    if (!header) return;
    let last = null;
    const update = () => {
      const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      if (bottom === last) return;
      last = bottom;
      this.style.setProperty('--facets-sticky-top', `${bottom}px`);
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  openDrawer() {
    this.setAttribute('drawer-open', '');
    document.body.classList.add('overflow-hidden');
  }

  closeDrawer() {
    if (!this.hasAttribute('drawer-open')) return;
    this.removeAttribute('drawer-open');
    document.body.classList.remove('overflow-hidden');
  }

  static closeDropdowns(except) {
    document.querySelectorAll('facet-dropdown[open]').forEach((dropdown) => {
      if (dropdown !== except) dropdown.close();
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.active-facets').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const gridContainer = document.getElementById('ProductGridContainer');
    const grid = gridContainer ? gridContainer.querySelector('.collection') : null;
    if (grid) grid.classList.add('loading');
    document.querySelectorAll('.thb-filter-count').forEach((item) => item.classList.add('loading'));
    document.querySelectorAll('facet-filters-form').forEach((form) => form.setAttribute('loading', ''));

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderAll(html, event);
      })
      .catch(() => {
        document.querySelectorAll('facet-filters-form').forEach((form) => form.removeAttribute('loading'));
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderAll(html, event);
  }

  static renderAll(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    document.querySelectorAll('facet-filters-form').forEach((form) => form.removeAttribute('loading'));
    FacetFiltersForm.keepGridInView();
  }

  static renderProductGridContainer(parsedHTML) {
    const target = document.getElementById('ProductGridContainer');
    const source = parsedHTML.getElementById('ProductGridContainer');
    if (!target || !source) return;
    target.innerHTML = source.innerHTML;

    if (typeof ScrollTrigger !== 'undefined' && typeof gsap !== 'undefined') {
      ScrollTrigger.batch('.animations-true .product-card .product-featured-image-link, .animations-true .products .gallery--item figure', {
        start: 'top 90%',
        onEnter: (elements) => {
          gsap.to(elements, { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
        }
      });
    }

    document.getElementById('Facets-Toggle-Fixed')?.setElements();
  }

  static renderProductCount(parsedHTML) {
    const countHtml = parsedHTML.getElementById('ProductCount');
    if (!countHtml) return;
    document.querySelectorAll('.thb-filter-count').forEach((item) => {
      item.innerHTML = countHtml.innerHTML;
      item.classList.remove('loading');
    });
  }

  static renderFilters(parsedHTML, event) {
    const sourceDropdown = event ? event.target.closest('facet-dropdown') : null;
    const sourceIndex = sourceDropdown ? sourceDropdown.dataset.index : null;

    parsedHTML.querySelectorAll('facet-dropdown').forEach((fresh) => {
      const index = fresh.dataset.index;
      const current = document.querySelector(`facet-dropdown[data-index="${index}"]`);
      if (!current) return;

      // Count badge + active state always refresh.
      const freshCount = fresh.querySelector('.facet-dropdown__count');
      const currentCount = current.querySelector('.facet-dropdown__count');
      if (freshCount && currentCount) {
        currentCount.textContent = freshCount.textContent;
        currentCount.hidden = freshCount.hidden;
      }
      current.classList.toggle('facet-dropdown--active', fresh.classList.contains('facet-dropdown--active'));

      // The panel the customer is working in keeps its DOM (focus, scroll, slider handles);
      // every other panel takes the server's fresh values and counts.
      if (index === sourceIndex) return;
      const freshPanel = fresh.querySelector('.facet-dropdown__panel-inner');
      const currentPanel = current.querySelector('.facet-dropdown__panel-inner');
      if (freshPanel && currentPanel) currentPanel.innerHTML = freshPanel.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (typeof SelectWidth !== 'undefined') {
      const sortSelect = document.getElementById('SortBy');
      if (sortSelect) SelectWidth.prototype.resizeSelect(sortSelect);
    }
  }

  static renderActiveFacets(parsedHTML) {
    const fresh = parsedHTML.querySelector('.active-facets');
    const current = document.querySelector('.active-facets');
    if (fresh && current) {
      current.innerHTML = fresh.innerHTML;
      current.className = fresh.className;
    }
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(parsedHTML) {
    ['#Facets-Toggle', '#Facets-Toggle-Fixed', '.thb-filter-sort-count--bar', '.facets-drawer__footer'].forEach((selector) => {
      const fresh = parsedHTML.querySelector(selector);
      const current = document.querySelector(selector);
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    });
  }

  /* After a filter change while scrolled deep into the grid, bring the top of the results back under the bar. */
  static keepGridInView() {
    const bar = document.querySelector('facet-filters-form.facets--bar');
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!bar || !gridContainer || !facetsIsDesktop()) return;
    const barRect = bar.getBoundingClientRect();
    const gridTop = gridContainer.getBoundingClientRect().top;
    if (gridTop < barRect.bottom) {
      window.scrollTo({ top: window.scrollY + gridTop - barRect.bottom - 8, behavior: 'smooth' });
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    const bar = document.querySelector('facet-filters-form.facets--bar');
    const id = (grid && grid.dataset.id) || (bar && bar.dataset.section);
    return [{ section: id }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);

    if (searchParams.get('filter.v.price.gte') === '0.00' || searchParams.get('filter.v.price.gte') === '') {
      searchParams.delete('filter.v.price.gte');
    }
    const slider = document.querySelector('.price_slider');
    if (slider) {
      const lte = searchParams.get('filter.v.price.lte');
      if (lte === parseFloat(slider.dataset.max).toFixed(2) || lte === '') {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event, link) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = (link || event.currentTarget).href;
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 * Removable chips + "Clear all" + drawer "Clear". Delegated, so links re-rendered by AJAX still work.
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link) return;
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      if (!form) return;
      form.onActiveFilterClick(event, link);
    });
  }
}
customElements.define('facet-remove', FacetRemove);

/**
 * One filter: button + panel. Desktop = dropdown (one open at a time, closes on outside click / Esc).
 * Mobile = accordion row inside the sheet.
 */
class FacetDropdown extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.facet-dropdown__button');
    this.panel = this.querySelector('.facet-dropdown__panel');
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.hasAttribute('open') ? this.close() : this.open();
    });
  }

  open() {
    if (facetsIsDesktop()) FacetFiltersForm.closeDropdowns(this);
    this.setAttribute('open', '');
    this.button.setAttribute('aria-expanded', 'true');
    if (facetsIsDesktop()) this.position();
  }

  close() {
    this.removeAttribute('open');
    this.button.setAttribute('aria-expanded', 'false');
    this.classList.remove('facet-dropdown--align-right');
    const inner = this.panel.querySelector('.facet-dropdown__panel-inner');
    if (inner) inner.style.maxHeight = '';
  }

  /* Keep the panel on screen: flip to the right edge if it would overflow, cap its height to the viewport. */
  position() {
    this.classList.remove('facet-dropdown--align-right');
    const rect = this.panel.getBoundingClientRect();
    if (rect.right > window.innerWidth - 16) this.classList.add('facet-dropdown--align-right');
    const inner = this.panel.querySelector('.facet-dropdown__panel-inner');
    if (inner) {
      const top = inner.getBoundingClientRect().top;
      inner.style.maxHeight = `${Math.max(160, window.innerHeight - top - 40)}px`;
    }
  }
}
customElements.define('facet-dropdown', FacetDropdown);

/**
 * Container of all dropdowns; on mobile it is the bottom sheet.
 */
class FacetDropdowns extends HTMLElement {
  constructor() {
    super();
  }
}
customElements.define('facet-dropdowns', FacetDropdowns);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': 'lower' },
          { 'aria-label': 'upper' }
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (typeof noUiSlider === 'undefined') return;
    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function () {
      /* Dispatch from inside the panel so renderFilters knows which facet is being edited. */
      const inputEvent = new Event('input', { bubbles: true });
      amounts.querySelector('.field__input_max').dispatchEvent(inputEvent);
    });
  }
}
customElements.define('price-slider', PriceSlider);

/**
 *  Mobile "Filter" buttons (inline + the fixed pill that follows the grid).
 */
if (!customElements.get('facet-toggle')) {
  class FacetToggle extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.setElements();
      this.addEventListener('click', this.onClick.bind(this));
      if (this.classList.contains('facets-toggle--fixed')) {
        window.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      }
    }
    setElements() {
      this.form = this.closest('facet-filters-form');
      const container = this.closest('.collection-container');
      this.wrapper = container ? container.querySelector('#product-grid') : null;
    }
    onClick() {
      if (this.form) this.form.openDrawer();
    }
    onScroll() {
      if (!this.wrapper || facetsIsDesktop()) {
        this.classList.remove('sticky');
        return;
      }
      let total = this.wrapper.offsetTop + this.wrapper.offsetHeight - window.innerHeight + 90;
      if (window.scrollY < this.wrapper.offsetTop) {
        this.classList.remove('sticky');
      } else if (window.scrollY > this.wrapper.offsetTop && window.scrollY < total) {
        this.classList.add('sticky');
      } else if (window.scrollY >= total) {
        this.classList.remove('sticky');
      }
    }
  }
  customElements.define('facet-toggle', FacetToggle);
}
